import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { marketsApi, type MarketView, type UserSalesScopeView } from "../../api/markets";
import { COUNTRIES, flagEmoji } from "../../i18n/countries";
import { useToast } from "../Toast";
import type { ApiError } from "../../api/client";
import { Select } from "../Select";

function countryName(code: string | null | undefined): string {
  if (!code) return "";
  return COUNTRIES.find((c) => c.code === code)?.name ?? code;
}

type Props = {
  userId: string;
};

/**
 * Sales scope manager: picks markets first, then countries.
 * Each scope = one row in user_sales_scope. User can hold multiple of each.
 */
export function UserSalesScopePicker({ userId }: Props) {
  const { t } = useTranslation();
  const toast = useToast();

  const [markets, setMarkets] = useState<MarketView[]>([]);
  const [scopes, setScopes] = useState<UserSalesScopeView[]>([]);
  const [pick, setPick] = useState<string>("");

  async function refresh() {
    const [ms, sc] = await Promise.all([marketsApi.list(), marketsApi.listUserScopes(userId)]);
    setMarkets(ms);
    setScopes(sc);
  }

  useEffect(() => { void refresh(); }, [userId]);

  // Build the dropdown options: markets first, then countries.
  // Skip what's already assigned to this user.
  const options = useMemo(() => {
    const usedMarkets = new Set(scopes.filter((s) => s.scopeType === "MARKET").map((s) => s.marketId!));
    const usedCountries = new Set(scopes.filter((s) => s.scopeType === "COUNTRY").map((s) => s.countryCode!));
    const out: { kind: "market" | "country"; value: string; label: string }[] = [];

    for (const m of markets) {
      if (usedMarkets.has(m.id)) continue;
      const countries = m.countryCodes.length > 0
        ? ` (${m.countryCodes.join(", ")})`
        : "";
      out.push({ kind: "market", value: `M:${m.id}`, label: `${m.name}${countries}` });
    }
    for (const c of COUNTRIES) {
      if (usedCountries.has(c.code)) continue;
      out.push({ kind: "country", value: `C:${c.code}`, label: `${flagEmoji(c.code)} ${c.name}` });
    }
    return out;
  }, [markets, scopes]);

  async function add() {
    if (!pick) return;
    try {
      if (pick.startsWith("M:")) {
        await marketsApi.addUserMarket(userId, pick.slice(2));
      } else if (pick.startsWith("C:")) {
        await marketsApi.addUserCountry(userId, pick.slice(2));
      }
      setPick("");
      await refresh();
      toast.success(t("admin.salesScope.added", "Sales scope added."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function remove(scopeId: string) {
    try {
      await marketsApi.removeUserScope(userId, scopeId);
      await refresh();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {scopes.length === 0 ? (
        <div style={{ color: "var(--ink-3)", fontSize: 12 }}>
          {t("admin.salesScope.empty", "No sales scope assigned. User sees only directly-assigned companies.")}
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {scopes.map((s) => (
            <li key={s.id} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 10px",
              background: s.scopeType === "MARKET" ? "var(--brand-primary)" : "var(--surface-2)",
              color: s.scopeType === "MARKET" ? "var(--on-primary)" : "var(--ink-2)",
              border: "1px solid var(--border)",
              borderRadius: 999,
              fontSize: 12, fontWeight: 600,
            }}>
              {s.scopeType === "MARKET"
                ? <span>📍 {s.marketName}</span>
                : <span>{flagEmoji(s.countryCode)} {countryName(s.countryCode)}</span>}
              <button type="button" onClick={() => remove(s.id)} aria-label={t("action.remove", "Remove")}
                      style={{
                        background: "transparent", border: "none",
                        color: "inherit", cursor: "pointer",
                        padding: 0, fontSize: 14, lineHeight: 1,
                      }}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div style={{ display: "flex", gap: 6 }}>
        <Select<string>
          value={pick}
          onChange={setPick}
          placeholder={t("admin.salesScope.placeholder", "— pick market or country —")}
          ariaLabel={t("admin.salesScope.placeholder", "— pick market or country —")}
          style={{ flex: 1 }}
          options={[
            { value: "", label: t("admin.salesScope.placeholder", "— pick market or country —") },
            ...options.map((o) => ({
              value: o.value,
              label: o.label,
              group: o.kind === "market"
                ? t("admin.salesScope.markets", "Markets")
                : t("admin.salesScope.countries", "Countries"),
            })),
          ]}
        />
        <button type="button" className="btn" onClick={add} disabled={!pick}>
          {t("admin.salesScope.add", "Add")}
        </button>
      </div>
    </div>
  );
}
